import React, { useRef, useEffect, useState } from 'react';
import './EventLegends.css';
import { getValueFromResource } from '../../common/timeLiveFunction';
import { useAppDispatch } from '../../app/hooks';
import { useTranslation } from 'react-i18next';

interface EventLegendsPopupProps {
    GoogleColorCode?: string;
    OutlookColorCode?: string;
    showTimesheet?: boolean;
    showTimeoff?: boolean;
    onClose: () => void;
    buttonRef?: HTMLElement | null;
}

const EventLegendsPopup: React.FC<EventLegendsPopupProps> = ({
    GoogleColorCode = '#008978',
    OutlookColorCode = '#81BFDA',
    showTimesheet = true,
    showTimeoff = true,
    onClose,
    buttonRef
}) => {
    const popupRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    useEffect(() => {
        const handleMouseLeave = (event: MouseEvent) => {
            const target = event.relatedTarget as HTMLElement;

            if (
                (buttonRef && buttonRef.contains(target)) ||
                (popupRef.current && popupRef.current.contains(target))
            ) {
                return;
            }

            setTimeout(() => {
                const mouseX = event.clientX;
                const mouseY = event.clientY;
                const elementUnderMouse = document.elementFromPoint(mouseX, mouseY);

                const isOnButton = buttonRef && buttonRef.contains(elementUnderMouse);
                const isOnPopup = popupRef.current && popupRef.current.contains(elementUnderMouse);

                if (!isOnButton && !isOnPopup) {
                    onClose();
                }
            }, 150);
        };

        if (popupRef.current) {
            popupRef.current.addEventListener('mouseleave', handleMouseLeave);
        }

        return () => {
            if (popupRef.current) {
                popupRef.current.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, [onClose, buttonRef]);

    useEffect(() => {
        const calculatePosition = () => {
            if (popupRef.current && buttonRef) {
                const buttonRect = buttonRef.getBoundingClientRect();
                const popupRect = popupRef.current.getBoundingClientRect();
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;

                let top = buttonRect.bottom + 8;
                let left = buttonRect.right - 260;

                if (left + 260 > viewportWidth - 10) {
                    left = viewportWidth - 270;
                }

                if (left < 10) {
                    left = 10;
                }

                if (top + popupRect.height > viewportHeight - 10) {
                    top = buttonRect.top - popupRect.height - 8;
                }

                if (top < 10) {
                    top = 10;
                }

                setPosition({ top, left });
            }
        };

        calculatePosition();
        window.addEventListener('resize', calculatePosition);
        window.addEventListener('scroll', calculatePosition);

        return () => {
            window.removeEventListener('resize', calculatePosition);
            window.removeEventListener('scroll', calculatePosition);
        };
    }, [buttonRef]);

    const legends = [
        {
            id: 'internal',
            label: 'TimeLive Calendar Events',
            color: '#3F51B5',
            show: true
        },
        {
            id: 'google',
            label: 'Google Calendar Events',
            color: GoogleColorCode,
            show: true
        },
        {
            id: 'outlook',
            label: 'Outlook Calendar Events',
            color: OutlookColorCode,
            show: true
        },
        {
            id: 'timesheet',
            label: 'Time Entries',
            color: '#f1c232',
            show: showTimesheet
        },
        {
            id: 'timeoff',
            label: 'Time Off',
            color: '#FF6D00',
            show: showTimeoff
        }
    ];

    const visibleLegends = legends.filter(legend => legend.show);

    return (
        <div
            ref={popupRef}
            className="event-legends-popup"
            style={{
                position: 'fixed',
                top: `${position.top - 5}px`,
                left: `${position.left + 45}px`,
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                padding: '16px',
                minWidth: '210px',
                maxWidth: '350px',
                zIndex: 10000
            }}
        >
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '12px',
                color: '#1f2937',
                borderBottom: '1px solid #e5e7eb',
                paddingBottom: '8px'
            }}>
                <span>{getValueFromResource('Event Legends', dispatch, t)}</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {visibleLegends.map((legend, index) => (
                    <div
                        key={legend.id}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '5px 0',
                            borderBottom: index !== visibleLegends.length - 1 ? '1px solid #f3f4f6' : 'none'
                        }}
                    >
                        <div
                            style={{
                                width: '15px',
                                height: '15px',
                                backgroundColor: legend.color,
                                borderRadius: '4px',
                                flexShrink: 0,
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                            }}
                        />
                        <span style={{
                            fontSize: '13px',
                            color: '#374151',
                            lineHeight: '1.4',
                            fontWeight: '400'
                        }}>
                            {getValueFromResource(`${legend.label}`, dispatch, t)}
                            
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default EventLegendsPopup;